import { Allotment } from 'allotment';
import type React from 'react';
import { useMemo, useRef } from 'react';
import 'allotment/dist/style.css';
import { createJSONSchema, createUISchema } from '@carboncopyco/editor-utils';
import type { JsonFormProps } from '../JsonForm';
import { JsonForm } from '../JsonForm';
import { EmptyState } from './EmptyState';
import {
  FormBuilderContext,
  type FormBuilderContextProps,
} from './FormBuilderContext';
import { JsonTree } from './JsonTree';

const allotmentStyles = `
:root {
  /* Color variables for allotment package */
  /* https://github.com/johnwalley/allotment#styling */
  --focus-border: #007fd4;
  --separator-border: transparent;
}

.split-view-view {
  height: 100%;
}
`;

interface PaneProps {
  title: string;
  children: React.ReactNode;
}

const Pane = ({ title, children }: PaneProps) => {
  return (
    <Allotment.Pane>
      <div className="h-full flex flex-col gap-1.5">
        <span className="text-ds-natural-400 text-xs">{title}</span>
        {children}
      </div>
    </Allotment.Pane>
  );
};

export type FormBuilderProps = Omit<JsonFormProps, 'mode'> &
  Pick<FormBuilderContextProps, 'onLearnMoreClick'>;

export const FormBuilder = ({
  value,
  onChange,
  onLearnMoreClick,
}: FormBuilderProps) => {
  const formRef = useRef<HTMLDivElement>(null);

  const addField = (jsonPath: string) => {
    const jsonSchema = createJSONSchema(value.json, jsonPath);
    const uiSchema = createUISchema(value.json, jsonPath);
    onChange({
      ...value,
      fields: [...value.fields, { jsonPath, jsonSchema, uiSchema }],
    });
    // scroll to the bottom of the form builder in order to see the last added field
    setTimeout(scrollFormToBottom, 200);
  };

  const removeField = (jsonPath: string) => {
    onChange({
      ...value,
      fields: value.fields.filter((field) => field.jsonPath !== jsonPath),
    });
  };

  const scrollFormToBottom = () => {
    formRef.current?.scrollTo({
      behavior: 'smooth',
      top: formRef.current.scrollHeight,
    });
  };

  const contextValue = useMemo(
    () => ({ onLearnMoreClick }),
    [onLearnMoreClick]
  );

  return (
    <FormBuilderContext.Provider value={contextValue}>
      <style>{allotmentStyles}</style>
      <Allotment>
        <Pane title="JSON STRUCTURE">
          <div className="overflow-scroll flex-1 border py-2 px-2.5 rounded-l-ds-md bg-ds-natural-50">
            <JsonTree
              value={value}
              onAddField={addField}
              onRemoveField={removeField}
            />
          </div>
        </Pane>
        <Pane title="PREVIEW">
          <div
            className="overflow-scroll flex-1 border py-2 px-2.5 rounded-r-ds-md"
            ref={formRef}
          >
            {value.fields.length ? (
              <JsonForm
                value={value}
                onChange={onChange}
                mode="builder"
              />
            ) : (
              <EmptyState />
            )}
          </div>
        </Pane>
      </Allotment>
    </FormBuilderContext.Provider>
  );
};
